import { useImperativeHandle, useRef, useState, type Ref } from 'react';
import { ApiError } from '@discovr/common';
import { isStaleRowError, useCampaignCapabilitiesStore } from '@discovr/features';
import type { RollbackReasonCode } from '@discovr/types';
import { useMenuDialogFocus } from '../../hooks/useMenuDialogFocus';
import { rowDisplayName } from '../../utils/rowDisplayName';
import { RollbackApprovalDialog } from '../RollbackApprovalDialog';
import { profileOfferabilityAtPending } from './creatorLookupStatus';
import type { CreatorsRow } from './types';

export type RowOpener = (row: CreatorsRow, opener: HTMLElement | null) => void;

export interface DialogHandle {
  openRollback: RowOpener;
}

interface Subject {
  entryId: string;
  creatorName: string;
}

const isProfileUnofferable = (err: unknown) => err instanceof ApiError && err.code === 'PROFILE_UNOFFERABLE';

export function BrandApprovalRollbackDialog({ ref }: { ref: Ref<DialogHandle> }) {
  const rollbackBrandApproval = useCampaignCapabilitiesStore((s) => s.rollbackBrandApproval);
  const [subject, setSubject] = useState<Subject | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const { rememberFocus, restoreFocus } = useMenuDialogFocus();

  useImperativeHandle(
    ref,
    () => ({
      openRollback: (row, opener) => {
        if (row.status !== 'accepted_by_brand' || !rollbackBrandApproval) return;
        if (!profileOfferabilityAtPending(row).offerable) return;
        rememberFocus(opener);
        setSubject({ entryId: row.id, creatorName: rowDisplayName(row) });
      },
    }),
    [rollbackBrandApproval, rememberFocus],
  );

  const confirm = async (reasonCode: RollbackReasonCode, note: string) => {
    const submitted = subject;
    if (!submitted || !rollbackBrandApproval || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    const close = () => setSubject((current) => (current === submitted ? null : current));
    try {
      await rollbackBrandApproval({ entryId: submitted.entryId, reasonCode, ...(note ? { note } : {}) });
      close();
    } catch (err) {
      // Toasted by the mutation hook. Retrying a stale row or a profile the brand can't see fails again.
      if (isStaleRowError(err) || isProfileUnofferable(err)) close();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <RollbackApprovalDialog
      open={subject !== null}
      onOpenChange={(open) => !open && setSubject(null)}
      creatorName={subject?.creatorName ?? ''}
      kind={subject ? 'brand_approval' : null}
      busy={busy}
      onConfirm={confirm}
      onCloseAutoFocus={restoreFocus}
    />
  );
}
